import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useState, type ReactNode } from 'react'

import { ErrorState } from '@/components/feedback/ErrorState'
import { Loading } from '@/components/feedback/Loading'
import { DateRangePicker } from '@/components/filters/DateRangePicker'
import { MultiSelect } from '@/components/filters/MultiSelect'
import { PAGE_PARAMS, PAGE_PARAM_KEYS } from '@/features/report/reconcile/params'
import { ReconcileBody } from '@/features/report/reconcile/ReconcileBody'
import { ReportFilterBar } from '@/features/report/ReportFilterBar'
import { useReportFilters } from '@/features/report/use-report-filters'
import { useCubeLiveQuery } from '@/hooks/use-cube-live-query'
import type { ReconcileReport } from '@/lib/cube-report'
import { formatInteger, todayAsDateInput } from '@/lib/format'
import {
  KANTAR_VIEW,
  aktarimCandidates,
  applyOverrides,
  compareTr,
  diaFromDay,
  isLiveRange,
  istanbulNowMs,
  missingKantarKeys,
  reconcile,
  reweighCandidates,
  scopeResult,
  shiftDay,
  lateTartiRuns,
  type ReconcileResult,
} from '@/lib/kantar-dia'
import { messages } from '@/lib/messages'
import { queryKeys } from '@/lib/query-keys'
import { services } from '@/services'
import {
  AKTARIM_VIEW,
  MAX_ROWS,
  aktarimQuery,
  diaLateQuery,
  diaQuery,
  kantarLookupQuery,
  kantarQuery,
  reweighQuery,
  toAktarimRows,
  toDiaRows,
  toKantarRows,
} from '@/services/cube/kantar-dia'

const t = messages.reconcile

/** Bugünü kapsayan, canlı olmayan (uzun) aralığın sessiz yenileme aralığı. */
const QUIET_REFRESH_MS = 10 * 60_000
/** Sayfadan ayrılıp dönünce veri beklemeden görünsün (arka planda tazelenir). */
const CACHE_MS = 10 * 60_000

/**
 * "İrsaliye bekleniyor", sahada süresi ve H-L zamana bağlı: canlı aralıkta dakikada bir yeniden
 * hesapla. Diğerlerinde durumlar son yüklemedeki saate göre kalır (büyük aralıkta her dakika
 * baştan eşleştirmek ekranı donduruyordu).
 */
function useNowMs(ticking: boolean) {
  const [now, setNow] = useState(() => istanbulNowMs())
  useEffect(() => {
    if (!ticking) return
    const id = setInterval(() => setNow(istanbulNowMs()), 60_000)
    return () => clearInterval(id)
  }, [ticking])
  return now
}

export function ReconcileReportView({ report }: { report: ReconcileReport }) {
  const filters = useReportFilters(report, { extraKeys: PAGE_PARAM_KEYS })
  const { dateFrom, dateTo } = filters.state
  const kantars = filters.getList(PAGE_PARAMS.kantar)
  const timeFrom = filters.get(PAGE_PARAMS.timeFrom)
  const timeTo = filters.get(PAGE_PARAMS.timeTo)
  // Yalnız kısa ve bugünü kapsayan aralık canlı izlenir (bkz. `LIVE_MAX_DAYS`). Bugünü
  // kapsayan daha uzun aralık sessizce `QUIET_REFRESH_MS`'de bir yenilenir; geçmiş aralık
  // yüklendiği gibi kalır.
  const today = todayAsDateInput()
  const live = isLiveRange(dateFrom, dateTo, today)
  const bigQuery = {
    compact: true,
    subscribe: live,
    refetchInterval: !live && (!dateTo || dateTo >= today) ? QUIET_REFRESH_MS : false,
    gcTime: CACHE_MS,
  } as const
  const nowMs = useNowMs(live)
  const kantarAllowed = report.kantarFields !== undefined

  const kantar = useCubeLiveQuery({
    queryKey: queryKeys.cube(KANTAR_VIEW, { reconcile: 'range', dateFrom, dateTo }),
    query: kantarQuery(dateFrom, dateTo),
    select: toKantarRows,
    enabled: kantarAllowed,
    ...bigQuery,
    paginate: { maxRows: MAX_ROWS },
  })
  const diaFrom = diaFromDay(dateFrom)
  // Geçmiş aralıkta DİA toplu sorgusu `to+1`'de biter; sonradan kesilen satırlar ayrı sorguda
  // (`diaLate`). Aralık bugüne (ya da dünden bugüne tolerans günü) uzanıyorsa tek sorgu.
  const diaTo = dateTo && shiftDay(dateTo, 1) < today ? shiftDay(dateTo, 1) : undefined
  const dia = useCubeLiveQuery({
    queryKey: queryKeys.cube(report.view, { reconcile: 'dia', diaFrom, diaTo }),
    query: diaQuery(report.view, diaFrom, diaTo),
    select: toDiaRows(report.view),
    enabled: kantarAllowed,
    ...bigQuery,
    paginate: { maxRows: MAX_ROWS },
  })

  /**
   * Sorgu bu aralığın verisini taşıyor mu: `keepPreviousData` yüzünden tarih değişince her
   * sorgu kendi verisi gelene kadar önceki aralığınkini gösterir. Eşleştirme yalnız hepsi
   * güncelken yapılır; yoksa yeni kantar + eski DİA gibi karışık veriyle sayılar adım adım
   * değişiyordu (kullanıcı geri bildirimi, 2026-10-23).
   */
  const current = (query: { isSuccess: boolean; isPlaceholderData: boolean }) =>
    query.isSuccess && !query.isPlaceholderData

  const missing = useMemo(
    () => missingKantarKeys(kantar.data ?? [], dia.data ?? [], dateFrom, dateTo),
    [kantar.data, dia.data, dateFrom, dateTo],
  )

  const lookup = useCubeLiveQuery({
    queryKey: queryKeys.cube(KANTAR_VIEW, {
      reconcile: 'lookup',
      keys: Object.fromEntries(missing),
    }),
    query: kantarLookupQuery(missing),
    select: toKantarRows,
    // Eksik tartılar ancak kantar ve DİA aynı aralıktayken doğru hesaplanır.
    enabled: kantarAllowed && current(kantar) && current(dia) && missing.size > 0,
    ...bigQuery,
  })

  const runs = useMemo(
    () =>
      diaTo && current(kantar) && current(dia)
        ? lateTartiRuns(kantar.data ?? [], dia.data ?? [], dateFrom, dateTo)
        : undefined,
    // `current(...)` sorgu durumundan türer; veri değişince `data` da değişir.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      diaTo,
      kantar.data,
      dia.data,
      kantar.isPlaceholderData,
      dia.isPlaceholderData,
      dateFrom,
      dateTo,
    ],
  )
  const diaLate = useCubeLiveQuery({
    queryKey: queryKeys.cube(report.view, {
      reconcile: 'dia-late',
      after: diaTo,
      runs: runs ? Object.fromEntries(runs) : null,
    }),
    query: diaLateQuery(report.view, diaTo ?? '', runs ?? new Map()),
    select: toDiaRows(report.view),
    enabled: kantarAllowed && runs !== undefined,
    ...bigQuery,
    paginate: { maxRows: MAX_ROWS },
  })
  // Tek sorgudaki sıra (kalem anahtarı) korunur: eşleştirme ilk MLZM satırını alır.
  const diaRows = useMemo(
    () =>
      diaTo
        ? [...(dia.data ?? []), ...(diaLate.data ?? [])].sort(
            (a, b) => (a.diaKalemKey ?? 0) - (b.diaKalemKey ?? 0),
          )
        : (dia.data ?? []),
    [diaTo, dia.data, diaLate.data],
  )

  // Aktarım kaydı yalnız DİA satırı olmayan tartılar için (bkz. `aktarimCandidates`); kantar
  // ve DİA geldikten sonra, geç irsaliye aramalarıyla aynı turda.
  const candidates = useMemo(
    () =>
      current(kantar) && current(dia)
        ? aktarimCandidates(kantar.data ?? [], dia.data ?? [])
        : undefined,
    // `current(...)` sorgu durumundan türer; veri değişince `data` da değişir.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [kantar.data, dia.data, kantar.isPlaceholderData, dia.isPlaceholderData],
  )
  const aktarim = useCubeLiveQuery({
    queryKey: queryKeys.cube(AKTARIM_VIEW, {
      reconcile: 'aktarim',
      keys: candidates ? Object.fromEntries(candidates) : null,
    }),
    query: aktarimQuery(candidates ?? new Map()),
    select: toAktarimRows,
    enabled: kantarAllowed && candidates !== undefined && candidates.size > 0,
    ...bigQuery,
  })

  // Yeniden tartım kuralı (H-L): 24 saatten genç Sahada araçlarının aynı kantardaki sonraki
  // tartıları. Sahada kayıtları tarihten bağımsız geldiği için aralığı beklemez.
  const reweighKeys = useMemo(
    () => (current(kantar) ? reweighCandidates(kantar.data ?? [], nowMs) : undefined),
    // `current(...)` sorgu durumundan türer; veri değişince `data` da değişir.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [kantar.data, kantar.isPlaceholderData, nowMs],
  )
  const reweigh = useCubeLiveQuery({
    queryKey: queryKeys.cube(KANTAR_VIEW, {
      reconcile: 'reweigh',
      keys: reweighKeys ? Object.fromEntries(reweighKeys) : null,
    }),
    query: reweighQuery(reweighKeys ?? new Map()),
    select: toKantarRows,
    enabled: kantarAllowed && reweighKeys !== undefined && reweighKeys.size > 0,
    ...bigQuery,
  })

  // Elle düzeltmeler (backend); canlı değil, kaydedince bu anahtar tazelenir.
  const overrides = useQuery({
    queryKey: queryKeys.reconcileOverrides(),
    queryFn: () => services.reconcile.listOverrides(),
    enabled: kantarAllowed,
  })

  const ready =
    overrides.isSuccess &&
    current(kantar) &&
    current(dia) &&
    candidates !== undefined &&
    (candidates.size === 0 || current(aktarim)) &&
    reweighKeys !== undefined &&
    (reweighKeys.size === 0 || current(reweigh)) &&
    (!diaTo || current(diaLate)) &&
    (missing.size === 0 || current(lookup))
  const settled = useMemo(
    () =>
      ready
        ? reconcile({
            kantar: [...(kantar.data ?? []), ...(missing.size ? (lookup.data ?? []) : [])],
            dia: diaRows,
            aktarim: candidates?.size ? (aktarim.data ?? []) : [],
            reweighs: reweighKeys?.size ? (reweigh.data ?? []) : [],
            from: dateFrom,
            to: dateTo,
            nowMs,
          })
        : undefined,
    [
      ready,
      kantar.data,
      lookup.data,
      diaRows,
      aktarim.data,
      candidates,
      reweigh.data,
      reweighKeys,
      missing.size,
      dateFrom,
      dateTo,
      nowMs,
    ],
  )
  // Aynı aralıkta arka plan yenilemesi (canlı abonelik, sessiz yenileme) sürerken son tam
  // sonuç ekranda kalır; aralık değişince yükleniyor görünümü, ara sonuç hiç gösterilmez.
  const rangeKey = `${dateFrom ?? ''}|${dateTo ?? ''}`
  const [last, setLast] = useState<{ rangeKey: string; result: ReconcileResult }>()
  if (settled && last?.result !== settled) setLast({ rangeKey, result: settled })
  const result = settled ?? (last?.rangeKey === rangeKey ? last.result : undefined)

  const corrected = useMemo(
    () => result && applyOverrides(result, overrides.data ?? []),
    [result, overrides.data],
  )

  const kantarKey = kantars.join(',')
  const scoped = useMemo(
    () =>
      corrected &&
      scopeResult(corrected, { kantars, from: dateFrom, to: dateTo, timeFrom, timeTo }),
    // `kantarKey`: liste parametresi her çizimde yeni dizi döner.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [corrected, kantarKey, dateFrom, dateTo, timeFrom, timeTo],
  )
  // Süzülmemiş sonuçtan: yalnız DİA'da görünen (kantar kaydı olmayan) kantar da seçilebilsin.
  const kantarOptions = useMemo(
    () =>
      [...new Set((result?.records ?? []).map((record) => record.kantarAdi))]
        .sort(compareTr)
        .map((value) => ({ value, label: value })),
    [result],
  )

  const failed = [overrides, kantar, dia, diaLate, aktarim, lookup].find((query) => query.isError)
  const truncated = [kantar.data, dia.data, diaLate.data].some(
    (rows) => (rows?.length ?? 0) >= MAX_ROWS,
  )
  const timeActive = Boolean((dateFrom && timeFrom) || (dateTo && timeTo))

  // Tarih seçiciyi (saatli) bu sayfa kendisi çizer; ortak çubuğunki gizlenir.
  const dateFilter = report.filters.find((filter) => filter.type === 'dateRange')
  const barReport = useMemo(
    () => ({ ...report, filters: report.filters.filter((filter) => filter.type !== 'dateRange') }),
    [report],
  )

  let body: ReactNode
  if (!kantarAllowed) body = <p className="text-muted-foreground text-sm">{t.needsKantar}</p>
  else if (failed) body = <ErrorState error={failed.error} onRetry={() => void failed.refetch()} />
  else if (!scoped) body = <Loading rows={8} />
  else body = <ReconcileBody result={scoped} filters={filters} />

  return (
    <>
      <ReportFilterBar report={barReport} filters={filters}>
        <DateRangePicker
          idPrefix={`report-${report.view}`}
          from={dateFrom}
          to={dateTo}
          defaultRange={typeof dateFilter?.default === 'string' ? dateFilter.default : undefined}
          withTime
          timeFrom={timeFrom}
          timeTo={timeTo}
          // Tek güncelleme: tarih ve saat birlikte yazılır (hazır aralık saatleri sıfırlar).
          onChange={(range) =>
            filters.update({
              date_from: range.from,
              date_to: range.to,
              [PAGE_PARAMS.timeFrom]: range.timeFrom,
              [PAGE_PARAMS.timeTo]: range.timeTo,
            })
          }
        />
        <MultiSelect
          label={t.filter.kantar}
          options={kantarOptions}
          selected={kantars}
          onChange={(values) => filters.update({ [PAGE_PARAMS.kantar]: values })}
          withApply
        />
      </ReportFilterBar>
      {timeActive && <p className="text-muted-foreground text-xs">{t.filter.timeNote}</p>}
      {truncated && (
        <p className="text-destructive text-sm">{t.truncated(formatInteger(MAX_ROWS))}</p>
      )}
      {body}
    </>
  )
}
